/// FOURJS_START_COPYRIGHT(D,2015)
/// Property of Four Js*
/// (c) Copyright Four Js 2015, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("StartMenuGroupWidget", ["WidgetGroupBase", "WidgetFactory"], function (context, cls) {
  /**
   * StartMenuGroup widget.
   * @class StartMenuGroupWidget
   * @memberOf classes
   * @extends classes.WidgetGroupBase
   */
  cls.StartMenuGroupWidget = context.oo.Class(cls.WidgetGroupBase, function ($super) {
    return /** @lends classes.StartMenuGroupWidget.prototype */ {
      __name: "StartMenuGroupWidget",

      /**
       * Image of the startMenu command
       * @protected
       * @type {classes.ImageWidget}
       */
      _image: null,
      /**
       * Right icon collapse/expanded
       * @type {HTMLElement}
       */
      _leftIcon: null,

      /**
       * The element (button) containing the title and icon. Expand/collapse children container
       * @type {HTMLElement}
       */
      _headerElement: null,

      /**
       * The element containing the title
       * @type {HTMLElement}
       */
      _titleElement: null,

      /**
       * The element containing the folderIcon
       * @type {HTMLElement}
       */
      _folderIcon: null,

      /**
       * @inheritDoc
       */
      _initElement: function () {
        $super._initElement.call(this);
        this._headerElement = this.getElement().getElementsByClassName("gbc_StartMenuGroupWidget__header")[0];
        this._titleElement = this.getElement().getElementsByClassName("gbc_StartMenuGroupWidget__title")[0];
        this._folderIcon = this.getElement().getElementsByClassName("gbc_StartMenuGroupWidget__folderIcon")[0];
        this.setAcceptEventWhenWindowInactive(true);
        this._leftIcon = this._element.getElementsByClassName("left-icon")[0];

        const containerElement = this.getElement().getElementsByClassName("gbc_StartMenuGroupWidget__container")[0];
        containerElement.id = this.getRootClassName() + "__container";
        // APG Tree pattern: role="treeitem" + disclosure (aria-controls / aria-expanded) all live
        // on the focusable <button>; the surrounding <li role="none"> is a structural wrapper only.
        // aria-owns pairs the treeitem with its sibling <ul role="group"> in the a11y tree (the
        // group is a DOM sibling, not a descendant of the button — so explicit owns is required).
        cls.DOMElementHelper.updateAttribute(this._headerElement, "aria-controls", containerElement.id);
        cls.DOMElementHelper.updateAttribute(this._headerElement, "aria-owns", containerElement.id);
      },

      /**
       * @inheritDoc
       */
      destroy: function () {
        if (this._image) {
          this._image.destroy();
          this._image = null;
        }
        this._leftIcon = null;
        this._titleElement = null;
        this._headerElement = null;
        this._folderIcon = null;
        $super.destroy.call(this);
      },

      /**
       * The role-bearing element is the header treeitem <button>, not the
       * <li role="none"> root.
       * @inheritDoc
       */
      getAriaElement: function () {
        return this._headerElement;
      },

      /** @inheritDoc */
      _ariaDisabledExposed: true,

      /**
       * @inheritDoc
       */
      getDOMTabIndexElements: function () {
        return this._headerElement ? [this._headerElement] : [];
      },

      /**
       * @inheritDoc
       */
      manageMouseClick: function (domEvent) {
        if (domEvent.target === null) {
          return true;
        } // just ignore qa click

        if (cls.DOMElementHelper.elementOrParent(domEvent.target, "gbc_StartMenuGroupWidget__header") && this.isEnabled()) {
          // only click on group title are managed
          this.toggleMenu();
        }
        return false;
      },

      /**
       * Keyboard activation of the focused group header (accessibility mode
       * routes keys to the DOM-focused widget before dialog accelerators).
       * @inheritDoc
       */
      managePriorityKeyDown: function (keyString, domKeyEvent, repeat) {
        if (this.isEnabled() && this._isActivationKey(keyString, repeat) && document.activeElement === this._headerElement) {
          this.toggleMenu();
          return true;
        }
        return $super.managePriorityKeyDown.call(this, keyString, domKeyEvent, repeat);
      },

      /**
       * Toggles the menu state and updates the icon accordingly.
       */
      toggleMenu: function () {
        const open = this._element.classList.toggle("gbc_open");
        cls.DOMElementHelper.updateAttribute(this._headerElement, "aria-expanded", open);
        cls.DOMElementHelper.toggleClass(this._leftIcon, "zmdi-chevron-down", "zmdi-chevron-right", open);
      },

      /**
       * Set the text of the group
       * @param {string} text
       */
      setText: function (text) {
        this._setTextContent(
          text,
          function () {
            return this._titleElement;
          }.bind(this)
        );
      },

      /**
       * Get the text of the group
       * @return {string}
       */
      getText: function () {
        return this._titleElement.textContent;
      },

      /**
       * Define the command image
       * @param {string} image
       */
      setImage: function (image) {
        if (!image) {
          if (this._image) {
            this._headerElement.replaceChild(this._folderIcon, this._image.getElement());
            this._image.destroy();
            this._image = null;
          }
          return;
        }
        if (!this._image) {
          // Create the image if there is none
          this._image = cls.WidgetFactory.createWidget("Image", this.getBuildParameters());
          this._headerElement.replaceChild(this._image.getElement(), this._folderIcon);
        }
        this._image.setSrc(image);
      },

      /**
       * Get image of the command
       * @return {?string} - source of the image, null if not set
       */
      getImage: function () {
        if (this._image) {
          return this._image.getSrc();
        }
        return null;
      }
    };
  });
  cls.WidgetFactory.registerBuilder("StartMenuGroup", cls.StartMenuGroupWidget);
});
